import { useState, useEffect, useRef } from 'react'
import { useWizardStore } from '../../store/wizardStore'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import { api } from '../../lib/api'
import { RECIPIENT_TYPE_LABELS, YEAR_GROUP_LABELS, type Order } from '../../lib/types'

// SumUp widget will be loaded via script tag
declare global {
  interface Window {
    SumUpCard?: {
      mount: (config: any) => void
    }
  }
}

export default function Step8Payment() {
  const { email, fullName, orderId, previousStep, reset, setSlotHeldUntil } = useWizardStore()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pricing, setPricing] = useState<any>(null)
  const [checkoutCreated, setCheckoutCreated] = useState(false) // Track if SumUp checkout was created
  const successTitleRef = useRef<HTMLHeadingElement>(null)

  // The success view replaces the Pay button in place, so move focus to it and announce it
  useEffect(() => {
    if (isComplete) successTitleRef.current?.focus()
  }, [isComplete])

  useEffect(() => {
    // Load SumUp widget script
    const script = document.createElement('script')
    script.src = 'https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js'
    script.async = true
    document.body.appendChild(script)

    return () => {
      document.body.removeChild(script)
    }
  }, [])

  useEffect(() => {
    // Fetch full order details from database
    const fetchOrderDetails = async () => {
      if (orderId) {
        try {
          const [fetchedOrder, fetchedPricing] = await Promise.all([
            api.getOrder(orderId),
            api.getPricing()
          ])
          setOrder(fetchedOrder)
          setPricing(fetchedPricing)
        } catch (err) {
          console.error('Failed to fetch order:', err)
          setError('Failed to load order. Please try again.')
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchOrderDetails()
  }, [orderId])

  const handlePayment = async () => {
    if (!orderId || !window.SumUpCard) {
      setError('Payment system not ready. Please refresh and try again.')
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      // 1. Create checkout with SumUp
      const { checkoutId, lockedUntil } = await api.createCheckout(orderId)
      if (lockedUntil) setSlotHeldUntil(lockedUntil)

      // Mark that checkout has been created - prevents going back and creating duplicates
      setCheckoutCreated(true)

      // 2. Clear the "Click below" message
      const sumupContainer = document.getElementById('sumup-card')
      if (sumupContainer) {
        sumupContainer.innerHTML = ''
      }

      // 3. Mount SumUp Card widget
      window.SumUpCard.mount({
        id: 'sumup-card',
        checkoutId: checkoutId,
        locale: 'en-GB',
        currency: 'GBP',
        onResponse: async (type: string, body: any) => {
          console.log('SumUp response:', type, body)

          if (type === 'success') {
            try {
              await api.verifyPayment(orderId, checkoutId)
              const sumupContainer = document.getElementById('sumup-card')
              if (sumupContainer) sumupContainer.style.display = 'none'
              setSlotHeldUntil(null)
              setIsComplete(true)
            } catch (err: any) {
              console.error('Failed to verify payment:', err)
              if (err.status === 'FAILED') {
                setError('Payment failed. Please try again.')
                setIsProcessing(false)
              } else {
                // Money may have been taken; the webhook / admin reconcile will finish the order.
                setError(`Your payment is being confirmed. You will receive a confirmation email shortly. If it does not arrive, please contact us quoting reference ${checkoutId}.`)
              }
            }
          } else if (type === 'error') {
            // Payment failed
            setError('Payment failed. Please try again.')
            setIsProcessing(false)
          }
        }
      })
    } catch (err: any) {
      console.error('Payment error:', err)
      setError(err.message || 'Failed to initialize payment. Please try again.')
      setIsProcessing(false)
    }
  }

  const handleStartOver = () => {
    reset()
  }

  const handleBack = () => {
    // Don't allow going back if checkout has been created (prevents duplicate checkout error)
    if (checkoutCreated) {
      return
    }

    // Reset any processing state when going back
    setIsProcessing(false)
    setError(null)

    // Always go back to previous step (tokens page - step 7)
    previousStep()
  }

  if (isComplete) {
    return (
      <>
        <CardHeader>
          <CardTitle ref={successTitleRef} className="text-center text-secondary-700">Payment Successful!</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto">
              <svg aria-hidden="true" className="w-10 h-10 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Thank you, {fullName}!
              </h3>
              <p className="text-gray-600">
                Your order has been confirmed. A confirmation email has been sent to {email}.
              </p>
            </div>
            <div className="bg-secondary-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                We look forward to seeing you at the Elves' Workshop!
              </p>
            </div>
            <Button
              onClick={handleStartOver}
              variant="outline"
              fullWidth
            >
              Make a new order
            </Button>
          </div>
        </CardContent>
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <CardHeader>
          <CardTitle>Payment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p role="status" className="text-gray-600">Loading order details...</p>
          </div>
        </CardContent>
      </>
    )
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Payment</CardTitle>
        <p className="text-gray-600 mt-2">
          Review your order and complete payment.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Order Summary */}
          <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-secondary text-secondary-foreground px-4 py-3">
              <h3 className="font-semibold flex items-center">
                <svg aria-hidden="true" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Order Summary
              </h3>
            </div>

            {/* Customer Information */}
            <div className="bg-gray-50 border-b-2 border-gray-200 px-4 py-3">
              <div className="space-y-1">
                <div className="flex items-center">
                  <svg aria-hidden="true" className="w-4 h-4 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="font-semibold text-gray-900">{fullName}</span>
                </div>
                <div className="flex items-center">
                  <svg aria-hidden="true" className="w-4 h-4 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-700">{email}</span>
                </div>
              </div>
            </div>

            {/* Gifts Section */}
            {order?.gifts && order.gifts.length > 0 && (
              <div className="border-b border-gray-200">
                <div className="bg-gray-50 px-4 py-2">
                  <h4 className="font-semibold text-sm text-gray-700">Gifts ({order.gifts.length})</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {order.gifts.map((gift) => (
                    <div key={gift.id} className="px-4 py-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {gift.recipientType === 'foodbank'
                              ? 'Foodbank Donation'
                              : `Gift for ${gift.recipientName}`}
                          </p>
                          <p className="text-xs text-gray-600">
                            {RECIPIENT_TYPE_LABELS[gift.recipientType]}
                          </p>
                          {gift.recipientType !== 'foodbank' && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              From: {gift.fromName} ({YEAR_GROUP_LABELS[gift.yearGroup]})
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Pickup: {gift.pickupLocation === 'school' ? 'School' : 'At the Fayre'}
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="font-semibold text-sm text-gray-900">
                            £{gift.price.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Santa Visit Section */}
            {order?.santaBooking && (
              <div className="border-b border-gray-200">
                <div className="bg-gray-50 px-4 py-2">
                  <h4 className="font-semibold text-sm text-gray-700">Santa Visit</h4>
                </div>
                <div className="px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {order.santaBooking.timeSlot?.time} - Slot {order.santaBooking.timeSlot?.slotNumber}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Children: {JSON.parse(order.santaBooking.childrenNames).join(', ')}
                      </p>
                      {JSON.parse(order.santaBooking.childrenNames).length > 0 && pricing && (
                        <p className="text-xs text-gray-500">
                          £{pricing.santa_per_child.toFixed(2)} × {JSON.parse(order.santaBooking.childrenNames).length} {JSON.parse(order.santaBooking.childrenNames).length === 1 ? 'child' : 'children'}
                        </p>
                      )}
                      {order.santaBooking.dietaryRequirements && (
                        <p className="text-xs text-gray-500 mt-1">
                          Dietary: {order.santaBooking.dietaryRequirements}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold text-sm text-gray-900">
                        £{pricing ? (pricing.santa_per_child * JSON.parse(order.santaBooking.childrenNames).length).toFixed(2) : '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tokens Section */}
            {order && order.tokenQuantity > 0 && pricing && (
              <div className="border-b border-gray-200">
                <div className="bg-gray-50 px-4 py-2">
                  <h4 className="font-semibold text-sm text-gray-700">Activity Tokens</h4>
                </div>
                <div className="px-4 py-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Activity Tokens
                      </p>
                      <p className="text-xs text-gray-500">
                        £{pricing.tokens.toFixed(2)} × {order.tokenQuantity} {order.tokenQuantity === 1 ? 'token' : 'tokens'}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-semibold text-sm text-gray-900">
                        £{(pricing.tokens * order.tokenQuantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Total Section */}
            <div className="bg-gray-50 px-4 py-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900">Total</span>
                <span className="text-2xl font-bold text-secondary-foreground">
                  £{order?.totalAmount.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Widget Container */}
          <div id="sumup-card" className="min-h-[200px]">
            {!isProcessing && (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  Click below to proceed with payment
                </p>
              </div>
            )}
          </div>

          {error && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{error}</p>
            </div>
          )}

          <div className="space-y-2">
            <Button
              onClick={handlePayment}
              fullWidth
              size="lg"
              disabled={isProcessing || !order}
              aria-busy={isProcessing}
            >
              {isProcessing ? 'Processing Payment...' : order ? `Pay £${order.totalAmount.toFixed(2)}` : 'Pay'}
            </Button>
            <Button
              onClick={handleBack}
              fullWidth
              variant="ghost"
              disabled={checkoutCreated}
            >
              Back
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Your payment is secure and processed by SumUp
          </p>
        </div>
      </CardContent>
    </>
  )
}
